import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../utils/auth';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/reports');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username, password);
      navigate('/reports');
    } catch (requestError) {
      console.error('登录失败:', requestError);
      setError(
        `${t('auth.loginFailed')}: ${requestError.response?.data?.error || t('auth.invalidCredentials')}`
      );
    } finally {
      setLoading(false);
    }
  };

  const statItems = isZh
    ? [
        { label: '一站式工作台', value: '报告审核、个人资料和诊所协作集中处理' },
        { label: '实时更新', value: '新报告到达和诊室状态变化即时通知' },
        { label: '便捷高效', value: '简洁的操作流程，让日常工作更加流畅' }
      ]
    : [
        { label: 'All-in-one workspace', value: 'Review reports, update your profile, and coordinate with your clinic in one place.' },
        { label: 'Real-time updates', value: 'Get notified instantly when new reports arrive or consultation status changes.' },
        { label: 'Fast & efficient', value: 'A streamlined workflow designed to make your daily tasks easier.' }
      ];

  if (authLoading) {
    return (
      <div className="auth-screen">
        <div className="page-shell">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <LanguageSwitcher />
          </div>
          <section className="surface-panel">
            <div className="surface-panel__body loading-state">
              <div className="loading-orb" />
              <h2 className="auth-form__title">{t('auth.checkingLoginStatus')}</h2>
              <p className="auth-form__subtitle">
                {isZh ? '正在确认医生会话和权限状态。' : 'Checking the current doctor session and access state.'}
              </p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="page-shell" style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LanguageSwitcher />
        </div>

        <div className="auth-shell">
          <section className="surface-panel surface-panel--accent auth-panel">
            <div className="surface-panel__body auth-brand">
              <div className="auth-brand__copy">
                <span className="page-eyebrow">
                  {isZh ? 'Doctor Console' : 'Doctor Console'}
                </span>
                <h1 className="auth-brand__headline">
                  {isZh ? 'AI Clinic 医生工作台' : 'AI Clinic Doctor Workspace'}
                </h1>
                <p className="auth-brand__summary">
                  {isZh
                    ? '医生专属工作台，集中处理报告审核、患者管理和诊所协作。'
                    : 'Your dedicated workspace for reviewing reports, managing patients, and collaborating with your clinic.'}
                </p>
                <div className="auth-form__actions">
                  <button
                    type="button"
                    className="theme-button theme-button--primary"
                    onClick={() => navigate('/admin-login')}
                  >
                    {t('dashboard.adminLogin')}
                  </button>
                  <button
                    type="button"
                    className="theme-button theme-button--ghost"
                    onClick={() => navigate('/')}
                  >
                    {t('dashboard.title')}
                  </button>
                </div>
              </div>

              <div className="auth-brand__stats">
                {statItems.map((item) => (
                  <div key={item.label} className="auth-brand__stat">
                    <span className="auth-brand__stat-label">{item.label}</span>
                    <span className="auth-brand__stat-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="surface-panel auth-panel">
            <div className="surface-panel__body auth-form">
              <div className="auth-form__header">
                <span className="page-eyebrow">{t('dashboard.doctorLogin')}</span>
                <h2 className="auth-form__title">{t('auth.loginTitle')}</h2>
                <p className="auth-form__subtitle">
                  {isZh
                    ? '输入医生账号后可直接进入待审核报告和历史审核工作区。'
                    : 'Sign in with your doctor account to continue into pending and reviewed report queues.'}
                </p>
              </div>

              {error && (
                <div className="status-banner status-banner--error">
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="stack">
                <div className="field">
                  <label htmlFor="doctor-username" className="field__label">
                    {t('auth.username')}
                  </label>
                  <input
                    id="doctor-username"
                    type="text"
                    className="theme-input"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder={isZh ? '请输入用户名' : 'Enter your username'}
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="doctor-password" className="field__label">
                    {t('auth.password')}
                  </label>
                  <input
                    id="doctor-password"
                    type="password"
                    className="theme-input"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={isZh ? '请输入密码' : 'Enter your password'}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <div className="auth-form__actions">
                  <button
                    type="submit"
                    className="theme-button theme-button--primary theme-button--full"
                    disabled={loading}
                  >
                    {loading ? t('auth.loggingIn') : t('auth.login')}
                  </button>
                </div>
              </form>

              <div className="status-banner">
                <span>
                  {isZh
                    ? '医生和管理员使用不同的登录入口，各自拥有独立的操作权限。'
                    : 'Doctor and administrator accounts have separate login portals with independent permissions.'}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Login;
